import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { CONTRACT_AGENT_STEP, contractAgentSteps } from '@/config/constants';
import { useGetContractByAgentQuery } from '@/apis/extended/contractApi';
import { TransactionFlowProgress } from '@/components/ui/TransactionFlowProgress';
import { Loader } from '../../Loader';
import { ContractDetailAgentInfo } from './ContractDetailAgentInfo';
import { ContractDetailAgentOfferInfo } from './ContractDetailAgentOfferInfo';
import { ContractDetailAgentReview } from './ContractDetailAgentReview';
import { ContractDetailAgentSellerInfo } from './ContractDetailAgentSellerInfo';
import { ContractDetailAgentSummary } from './ContractDetailAgentSummary';
import { ContractDetailAgentSuccess } from './ContractDetailBuyerSuccess';

type ContractDetailAgentProps = {
  uid: string;
};

export const ContractDetailAgent = ({ uid }: ContractDetailAgentProps) => {
  const { data: contract, isLoading } = useGetContractByAgentQuery(uid);

  const [step, setStep] = useState(-1);

  const currentStep = useMemo(() => {
    if (!contract) return 0;

    if (contract.agentSign?.isStored) return CONTRACT_AGENT_STEP.COMPLETED;
    if (contract.agentSellerInfo?.isStored) return CONTRACT_AGENT_STEP.REVIEW;
    if (contract.agentInfo?.isStored) return CONTRACT_AGENT_STEP.SELLER_INFO;
    return CONTRACT_AGENT_STEP.AGENT_INFO;
  }, [contract]);

  useEffect(() => {
    if (!contract || step !== -1) return;

    setStep(currentStep);
  }, [contract, currentStep]);

  if (isLoading) return <Loader isFull />;
  if (!contract) return null;

  const handleClickStep = (newStep: number) => {
    if (newStep > currentStep) return;
    setStep(newStep);
  };

  return (
    <Box>
      <TransactionFlowProgress
        steps={contractAgentSteps}
        activeStep={step}
        onStepClick={handleClickStep}
      />
      <Box sx={{ mt: 8, mx: 'auto', maxWidth: '600px' }}>
        {step === CONTRACT_AGENT_STEP.AGENT_INFO && (
          <ContractDetailAgentInfo
            uid={uid}
            onNext={() => setStep(CONTRACT_AGENT_STEP.SELLER_INFO)}
          />
        )}
        {step === CONTRACT_AGENT_STEP.SELLER_INFO && (
          <ContractDetailAgentSellerInfo
            uid={uid}
            onPrev={() => setStep(CONTRACT_AGENT_STEP.AGENT_INFO)}
            onNext={() => setStep(CONTRACT_AGENT_STEP.REVIEW)}
          />
        )}
        {step >= CONTRACT_AGENT_STEP.REVIEW &&
          (currentStep === CONTRACT_AGENT_STEP.COMPLETED ? (
            <ContractDetailAgentSuccess />
          ) : (
            <ContractDetailAgentReview
              uid={uid}
              onPrev={() => setStep(CONTRACT_AGENT_STEP.SELLER_INFO)}
              onNext={() => {}}
            />
          ))}
      </Box>
    </Box>
  );
};
